import { FlightDetails, ConnectionFlight } from '@pages/Loads/types';
import axiosClient from './axios-client';
import { AxiosResponse } from 'axios';

const GET_FLIGHT_NUMBER_URL = '/Loads/GetFlightNumber';

const GET_LAST_FLIGHT_NUMBER_URL = '/Loads/GetLastFlightNumber';

const GET_DEPARTURE_CODES_URL = '/Loads/GetDepartureCodes';

const GET_ARRIVAL_CODES_URL = '/Loads/GetArrivalCodes';

const GET_LAST_ROUTE_URL = '/Loads/GetLastRoute';


interface FlightSearchParams {
  FlightNumber?: string;
  Source?: string;
  Destination?: string;
  Dates?: any;
  IsNearbyDeparture?: boolean;
  IsNearbyArrival?: boolean;
  Stops?: number;
  AirlineName?: string;
  LeaveTime?: string;
  ArrivalTime?: string;
  IncludeDirect?: boolean;
  IncludeConnections?: boolean;
  PageNumber: number;
  PageSize: number;
}

// interface FlightSearchResponse {
//   flights: FlightDetails[];
//   message: string;
//   totalCount?: number;
//   totalPages?: number;
//   currentPage?: number;
// }
interface FlightSearchResponse {
  isSuccess: boolean;
  message: string;
  status: number;
  data: {
    items: FlightDetails[];
    pageNumber: number;
    pageSize: number;
    total: number;
    connectionTotal?: number;
    connections?: ConnectionFlight[];
  };
  count: number;
}


interface PostRequestItem {
  airlineId: number;
  flightNumber: string;
  sourceCode: string;
  destinationCode: string;
  departureDate: string;  
  arrvalDate: string;    
  depterm: string;
  arrterm: string;
  duration: string;
  elptim: string;
  depcity: string;
  arrcity: string;
}

interface PostFlightRequest {
  postRequest: PostRequestItem[];
}


/* eslint-disable @typescript-eslint/no-empty-interface */
interface PostFlightResponse {}
/* eslint-enable @typescript-eslint/no-empty-interface */

const fetchFlights = async (params: FlightSearchParams): Promise<FlightSearchResponse> => {
  try {
    const response = await axiosClient.get<FlightSearchResponse>('/Loads/FlightSearch', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        
        Object.keys(params).forEach(key => {
          const value = params[key];
          
          if (Array.isArray(value)) {
            // For arrays, add each value with the same key name (without [])
            value.forEach(item => {
              searchParams.append(key, item);
            });
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        
        return searchParams.toString();
      }
    });
    // console.log("Full Response:", response);
    // return response as unknown as FlightSearchResponse;
    // 🔹 Apply condition
    if (params.IncludeDirect === false && params.IncludeConnections === true) {
      // Try to slice at the top-level (if axios interceptor unwraps response.data)
      if (response.data && Array.isArray((response.data as any).items)) {
        (response.data as any).items = (response.data as any).items.slice(0, 6);
      }
      if (response.data && Array.isArray((response.data as any).connections)) {
        (response.data as any).connections = (response.data as any).connections.slice(0, 6);
      }
      // Fallback: if data is nested under data.data
      if (response.data && (response.data as any).data) {
        if (Array.isArray((response.data as any).data.items)) {
          (response.data as any).data.items = (response.data as any).data.items.slice(0, 6);
        }
        if (Array.isArray((response.data as any).data.connections)) {
          (response.data as any).data.connections = (response.data as any).data.connections.slice(0, 6);
        }
      }
    }

    console.log("Full Response:", response);

    return response as unknown as FlightSearchResponse;
  } catch (error) {
    console.error('Error fetching flight data:', error);
    throw error;
  }
};

const postFlightRequest = async (requestData: PostFlightRequest): Promise<PostFlightResponse> => {
  try {
    
    const response = await axiosClient.post<PostFlightResponse>("/Loads/PostRequest", requestData);
    console.log("Post Request Response:", response);
    return response.data;  
  } catch (error) {
    console.error("Error posting flight request:", error);
    throw error;
  }
};



export interface DepartureCode {
  departureCode: string;
}

export interface GetDepartureCodesParams {
  search?: string;
  arrivalCode?: string;
}


export interface ArrivalCode {
  arrivalCode: string; 
}

export interface GetArrivalCodesParams {
  search?: string;
  departureCode?: string; 
}

export  {fetchFlights, postFlightRequest};


interface FlightNumber {
  flightNumber: string;
  date: string;
}

interface FlightRoute {
  source: string;
  destination: string;
  date: string;
}

interface FlightHistoryResponse {
  recentSearches: string[];
  lastSearchFlightNumber: FlightNumber | null;
  lastSearchRoute: FlightRoute | null;
}

export const getFlightNumberHistory = async (): Promise<{
  recentSearches: string[];
  lastFlightNumbers: FlightNumber[];
  lastRoutes: FlightRoute[];
}> => {
  try {
    // Since the axiosClient already handles the baseURL and Authorization header
    // through interceptors, we just need to specify the endpoint
    const response = await axiosClient.get<FlightHistoryResponse>(
      '/Loads/GetFlightNumberHistory'
    )as unknown as FlightHistoryResponse;

    // The response.data is automatically returned by your interceptor
    const data = response; // No need for .data since interceptor handles it

    return {
      recentSearches: data.recentSearches || [],
      lastFlightNumbers: data.lastSearchFlightNumber ? [data.lastSearchFlightNumber] : [],
      lastRoutes: data.lastSearchRoute ? [data.lastSearchRoute] : [],
    };
  } catch (error) {
    console.error("Error fetching flight history:", error);
    return {
      recentSearches: [],
      lastFlightNumbers: [],
      lastRoutes: [],
    };
  }
};



export const getFlightNumbers = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_FLIGHT_NUMBER_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching flight numbers:', error);
    throw error;
  }
};

export const getLastFlightNumber = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_LAST_FLIGHT_NUMBER_URL
    );

    return response;
  } catch (error) {
    console.error('Error fetching last flight number:', error);
    throw error;
  }
};


export const getDepartureCodes = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_DEPARTURE_CODES_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching departure codes:', error);
    throw error;
  }
};

export const getArrivalCodes = async (
  search: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_ARRIVAL_CODES_URL,
      {
        params: { search },
      }
    );

    return response;
  } catch (error) {
    console.error('Error fetching arrival codes:', error);
    throw error;
  }
};

export const getLastRoute = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(
      GET_LAST_ROUTE_URL
    );

    return response;
  } catch (error) {
    console.error('Error fetching last route:', error);
    throw error;
  }
};